/**
 * Directives session helpers — distinguish the org-wide view from an open
 * patient chart.
 *
 * The `patient` object passed into these helpers is Vuex `login/getAllData`
 * → `phrData.patient`, built by pacmny-be from LA_user (see DataManagerImpl
 * getPatientByPatientId). It exposes:
 *   - id        → LA_user.id (often patients.mid in MySQL)
 *   - externId  → ZV_user_externid (patients.psersonalID / unique_ID)
 *   - externId2 → ZV_user_externid2 (accountnumber)
 *
 * Directives APIs use directive_record.patient_pid → MySQL patients.pid (auto-
 * increment PK). That is NOT the same as externId or patient.id. Resolve it via
 * resolveDirectivePatientPid() in directivesApi.js.
 */
import { sessionActorFrom } from '@/utils/sessionIdentity';
import { peekSessionSelfProfile } from '@/stores/sessionSelf';

/**
 * True when a staff member has entered a patient's chart, so `allData.patient`
 * is that patient rather than the logged-in user.
 *
 * This is NOT a scope signal. It says a chart is open, not who the person
 * behind the session is — read scope comes from privilege 65, see
 * utils/directivesScope.js.
 */
export function isViewingPatientChart(allData) {
  return !!allData?.viewingPatientChart;
}

/** patients.psersonalID from the session (externId). Not directive patient_pid. */
export function getSessionPatientPersonalId(patient = {}) {
  const id = patient.externId ?? patient.psersonalID;
  return id == null || id === '' ? '' : String(id);
}

/**
 * The acting user's id, for directives API calls: the signed-in staff member
 * (or patient), whether or not a chart is open — never the chart.
 *
 * The token-backed session (`GET /me`, decision D4) through sessionActorFrom;
 * '' until it has loaded. Main fell back to `allData` (manager while charting,
 * else patient); on the port that made a staff member with no chart open into
 * the empty `allData.patient` stub, so the actor came out '' even once signed
 * in (review of 8614ccd6). The bridge does not send this id — the API takes
 * the actor from the token — but the pages compare it with a record's
 * uploader and signer.
 */
export function getDirectiveActorId(allData = {}, sessionSelf) {
  return sessionActorFrom(allData, peekSessionSelfProfile(sessionSelf)).unique_ID;
}

/**
 * Org accountnumber for directives API calls.
 * While charting, the chart patient's org (clinical subject). Otherwise the
 * signed-in person's organization, from the session.
 */
export function getDirectiveAccountNumber(allData = {}, sessionSelf) {
  const patient = allData.patient || {};
  if (isViewingPatientChart(allData)) {
    return patient.externId2 || patient.externid2 || '';
  }
  return sessionActorFrom(allData, peekSessionSelfProfile(sessionSelf)).accountnumber;
}

/**
 * The signed-in person's OWN chart id (`patients.id`) from the session profile
 * (`GET /me` → `patient_id`), or '' for anyone who is not a patient.
 *
 * The port's answer to keyon's "does the session resolve its own pid". It is
 * read from who is signed in, never from `allData.patient` — which is the OPEN
 * chart, so a staff member viewing a chart would otherwise look like that
 * patient. A caregiver has none.
 */
export function getOwnPatientPid(me) {
  const pid = me && me.patient_id;
  return pid == null || pid === '' ? '' : String(pid);
}

/**
 * The signed-in person's own user id from the session profile, or ''.
 *
 * What main's `document_uploaded_by_id` is compared with to offer an
 * uploader "Remove" on their own document: for staff it is also
 * `manager.externid`, but a patient's session has no manager, and
 * `allData.patient.externId` is a chart id, not a person.
 */
export function getSessionUserId(me) {
  const userId = me && me.id;
  return userId == null || userId === '' ? '' : String(userId);
}
